import React, { useState, useEffect } from "react";
import { Container, Row, Col } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import apiService from "../../../services/api";
import { useStates } from "../../../hooks/useStates";
import useCodeCommon from "../../../hooks/useCodeCommon";
import { useAppSelector } from "../../../hooks/useAuth";
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  InputAdornment,
  CircularProgress,
  Alert,
} from "@mui/material";
import { FileUploadInput, TimeRangeInput, SelectInput, SearchableSelectInput } from "../../../components/forms";
import { FILE_UPLOAD_CONFIG } from "../../../constants";
import ImagePreviewCard from "../../../components/common/ImagePreviewCard";
import DefaultImage from "../../../assets/images/DefaultImage.png";

const BASE_IMAGE_URL = `${(process.env.REACT_APP_BASE_IMAGE_URL || "").replace(/\/?$/, '/')}`;
const DEFAULT_IMAGE = "default.png";

const FormSection = ({ title, children }: { title?: string; children: React.ReactNode }) => (
  <Paper className="mb-4 shadow-sm border-0">
    <Box className="px-3 pt-4 pb-5">
      {title && (
        <Typography
          variant="h6"
          className="fw-bold mb-3"
          sx={{ color: "rgba(13,44,90,1)" }}
        >
          {title}
        </Typography>
      )}
      {children}
    </Box>
  </Paper>
);

const TextInput = ({
  field,
  formData,
  onChange,
  placeholder,
  type = "text",
  label,
  required = false,
  endAdornment,
  InputLabelProps,
  error,
}: {
  field: string;
  formData: any;
  onChange: (field: string, value: string) => void;
  placeholder?: string;
  type?: string;
  label?: string;
  required?: boolean;
  endAdornment?: React.ReactNode;
  InputLabelProps?: any;
  error?: string;
}) => (
  <TextField
    fullWidth
    size="small"
    type={type}
    variant="outlined"
    label={label}
    required={required}
    value={formData[field]}
    placeholder={placeholder}
    onChange={(e) => onChange(field, e.target.value)}
    error={Boolean(error)}
    helperText={error}
    InputProps={{
      endAdornment: endAdornment,
    }}
    InputLabelProps={InputLabelProps}
    sx={{
      "& .MuiFormLabel-asterisk": {
        color: "red",
      },
    }}
  />
);

const CreateHubPage: React.FC<{ onBack: () => void }> = ({ onBack }) => {
  const navigate = useNavigate();
  const { user } = useAppSelector((state: any) => state.auth);

  const [formData, setFormData] = useState({
    hubname: "",
    address1: "",
    address2: "",
    city: "",
    state: "",
    zipcode: "",
    status: "",
    operationhours: "",
    geofencestartradius: "",
    latitude: "",
    longitude: "",
    hubimage: "",
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const { states, loading: statesLoading, error: statesError } = useStates();
  const { options: hubStatusOptions, loading: hubStatusLoading, error: hubStatusError } = useCodeCommon("HubStatus");
  const [cities, setCities] = useState<Array<{city_id: number, city_name: string}>>([]);
  const [citiesLoading, setCitiesLoading] = useState(false);

  useEffect(() => {
    if (error === 'Please fill all required fields') {
      const requiredFields: (keyof typeof formData)[] = [
        'hubimage', 'hubname', 'status', 'geofencestartradius', 'address1',
        'state', 'zipcode', 'city', 'latitude', 'longitude', 'operationhours'
      ];
      const allFilled = requiredFields.every(f => {
        const fieldValue = formData[f];
        return fieldValue !== undefined && fieldValue !== null && String(fieldValue).trim() !== '';
      });

      if (allFilled) {
        setError(null);
      }
    }
  }, [formData, error]);

  const resolvePreviewSrc = (value: string): string => {
    const v = String(value || "").trim();
    if (!v) {
      return `${BASE_IMAGE_URL}${DEFAULT_IMAGE}`;
    }
    if (v.startsWith('http')) {
      return v;
    }
    const cleaned = v.replace(/^\/+/, '');
    return `${BASE_IMAGE_URL}${cleaned || DEFAULT_IMAGE}`;
  };

  const fetchCitiesByState = async (stateId: number) => {
    setCitiesLoading(true);
    setError(null);
  
    try {
      const response = await apiService.getCitiesByState(stateId);
      const citiesData = (Array.isArray(response) ? response : (response as any)?.cities);
      setCities(citiesData);
    } catch (error: any) {
      setError("Failed to load cities. Please try again.");
      setCities([]);
    } finally {
      setCitiesLoading(false);
    }
  };
  

  const handleInputChange = (field: string, value: string) => {
    setErrors((prev) => {
      const next = { ...prev };
      if (String(value ?? '').trim() !== '') delete (next as any)[field];
      if (field === 'state') delete (next as any)['city'];
      return next;
    });

    setFormData((prev) => {
      const updated = { ...prev, [field]: value };
      return updated;
    });
    
    if (field === 'state' && value) {
      const stateId = parseInt(value);
      if (!isNaN(stateId)) {
        fetchCitiesByState(stateId);
      } else {
        setCities([]);
      }
    }
  };

  const handleSave = async () => {
    setLoading(true);
    setError(null);

    try {
      const nextErrors: Record<string, string> = {};
      const requireField = (key: string, label: string) => {
        const value = (formData as any)[key];
        if (value === undefined || value === null || String(value).trim() === "") {
          nextErrors[key] = `${label} is required`;
        }
      };

      requireField('hubimage', 'Hub Image');
      requireField('hubname', 'Hub Name');
      requireField('status', 'Status');
      requireField('geofencestartradius', 'Geofence Radius');
      requireField('address1', 'Address 1');
      requireField('state', 'State');
      requireField('zipcode', 'Pin Code');
      requireField('city', 'City');
      requireField('latitude', 'Latitude');
      requireField('longitude', 'Longitude');
      requireField('operationhours', 'Operating Hours');

      setErrors(nextErrors);
      if (Object.keys(nextErrors).length > 0) {
        throw new Error('Please fill all required fields');
      }

      const apiData = {
        hubmasterid: 0,
        hubname: formData.hubname,
        latitude: parseFloat(formData.latitude) || 0,
        longitude: parseFloat(formData.longitude) || 0,
        address1: formData.address1,
        address2: formData.address2,
        city: parseInt(formData.city) || 0,
        state: parseInt(formData.state) || 0,
        country: 0,
        zipcode: formData.zipcode,
        status: (() => {
          const selectedStatus = hubStatusOptions.find(status => status.codedisplayname === formData.status);
          const codeValue = selectedStatus?.codevalue;
          const num = parseInt((codeValue ?? '').toString());
          return isNaN(num) ? 0 : num;
        })(),
        userid: user?.userid,
        operationhours: formData.operationhours,
        geofencestartradius: parseFloat(formData.geofencestartradius) || 0,
        geofencesendradius: parseFloat(formData.geofencestartradius) || 0,
        phonenumber: "",
        whatsappnumber: "",
        hubimage: formData.hubimage,
      };
      
      await apiService.createHub(apiData);
      
      navigate('/hub-management', { state: { refreshHubs: true } });
    } catch (err: any) {
      setError(err.message || "Failed to create hub");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container fluid>
      {(error || statesError || hubStatusError) && (
        <Alert 
          severity="error" 
          className="mb-3" 
          onClose={() => setError(null)}
        >
          {error || statesError || hubStatusError}
        </Alert>
      )}

      <FormSection>
        <Row className="g-3">
          <Col xs={12} md={6} lg={3} className="mb-3">
            <FileUploadInput
              field="hubimage"
              formData={formData}
              onChange={handleInputChange}
              required
              error={errors.hubimage}
              folderPath={FILE_UPLOAD_CONFIG.FOLDER_PATHS.HUBS}
              folderType={FILE_UPLOAD_CONFIG.FOLDER_TYPES.HUBS}
            />
          </Col>
          <Col xs={12} md={6} lg={3}>
            <TextInput
              field="hubname"
              formData={formData}
              onChange={handleInputChange}
              label="Hub Name"
              required
              error={errors.hubname}
              placeholder="Enter hub name"
            />
          </Col>
          
          <Col xs={12} md={6} lg={3} className="mb-3">
            <TimeRangeInput
              field="operationhours"
              formData={formData}
              onChange={handleInputChange}
              label="Operating Hours"
              required
              error={errors.operationhours}
              placeholder="e.g., 9:00 AM - 6:00 PM"
              endAdornment={
                <InputAdornment position="end">
                  <span style={{ color: "#666" }}>🕐</span>
                </InputAdornment>
              }
            />
          </Col>
          <Col xs={12} md={6} lg={3}>
            <SelectInput
              field="status"
              formData={formData}
              onChange={handleInputChange}
              label="Status"
              required
              disabled={hubStatusLoading}
              options={hubStatusOptions.map(status => ({ label: status.codedisplayname, value: status.codedisplayname }))}
              error={errors.status}
            />
          </Col>
          <Col xs={12} md={6} lg={3}>
            <TextInput
              field="geofencestartradius"
              formData={formData}
              onChange={handleInputChange}
              label="Geofence Radius"
              required
              error={errors.geofencestartradius}
              placeholder="Enter radius in meters"
            />
          </Col>
          <Col xs={12} md={6} lg={3}>
            <TextInput
              field="address1"
              formData={formData}
              onChange={handleInputChange}
              label="Address 1"
              required
              error={errors.address1}
              placeholder="Enter address line 1"
            />
          </Col>
          <Col xs={12} md={6} lg={3}>
            <TextInput
              field="address2"
              formData={formData}
              onChange={handleInputChange}
              label="Address 2"
              placeholder="Enter address line 2"
            />
          </Col>
          
          <Col xs={12} md={6} lg={3} className="mb-3">
            <SearchableSelectInput
              field="state"
              formData={formData}
              onChange={handleInputChange}
              label="State"
              required
              disabled={statesLoading}
              options={states.map(state => ({ label: state?.state_name || "", value: state?.state_id?.toString() || "" }))}
              error={errors.state}
            />
          </Col>
          <Col xs={12} md={6} lg={3}>
            <TextInput
              field="zipcode"
              formData={formData}
              onChange={handleInputChange}
              label="Pin Code"
              required
              error={errors.zipcode}
              placeholder="Enter pin code"
            />
          </Col>
          
          <Col xs={12} md={6} lg={3}>
            <SearchableSelectInput
              field="city"
              formData={formData}
              onChange={handleInputChange}
              label="City"
              required
              disabled={citiesLoading || !formData.state}
              options={cities.map(city => ({ label: city?.city_name || "", value: city?.city_id?.toString() || "" }))}
              error={errors.city}
            />
          </Col>
          
          <Col xs={12} md={6} lg={3} className="mb-3">
            <TextInput
              field="latitude"
              formData={formData}
              onChange={handleInputChange}
              label="Latitude"
              required
              error={errors.latitude}
              placeholder="Enter latitude"
            />
          </Col>
          <Col xs={12} md={6} lg={3}>
            <TextInput
              field="longitude"
              formData={formData}
              onChange={handleInputChange}
              label="Longitude"
              required
              error={errors.longitude}
              placeholder="Enter longitude"
            />
          </Col>
        </Row>
      </FormSection>

      <FormSection title="Attachments">
        <Row className="g-3">
          {formData.hubimage && (
            <Col xs={12} sm={6} md={4}>
              <ImagePreviewCard
                title="Hub Image"
                value={formData.hubimage}
                resolveSrc={resolvePreviewSrc}
                size={200}
                defaultImage={DefaultImage}
                onDelete={() => setFormData((prev) => ({ ...prev, hubimage: "" }))}
              />
            </Col>
          )}

          {!formData.hubimage && (
            <Col xs={12}>
              <Box sx={{ 
                p: 4, 
                textAlign: 'center',
                backgroundColor: '#f8f9fa',
                borderRadius: 2,
                border: '2px dashed #dee2e6'
              }}>
                <Typography variant="body1" sx={{ color: '#6c757d' }}>
                  No attachments uploaded yet
                </Typography>
              </Box>
            </Col>
          )}
        </Row>
      </FormSection>

      <Box className="d-flex justify-content-end gap-3 mt-4">
        <Button
          variant="contained"
          className="px-2 py-2 fw-medium text-white"
          onClick={handleSave}
          disabled={loading}
          sx={{
            backgroundColor: "rgba(13,44,90,1)",
            minWidth: "130px",
            "&:disabled": {
              backgroundColor: "rgba(13,44,90,0.5)",
            },
          }}
        >
          {loading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={16} color="inherit" />
              Saving...
            </Box>
          ) : (
            'Save'
          )}
        </Button>
        <Button
          variant="outlined"
          className="px-4 py-2 fw-medium"
          onClick={onBack}
          sx={{
            borderColor: "rgba(13,44,90,1)",
            color: "rgba(13,44,90,1)",
            "&:hover": {
              borderColor: "rgba(13,44,90,1)",
              backgroundColor: "rgba(13,44,90,0.04)",
            },
          }}
        >
          Cancel
        </Button>
      </Box>
    </Container>
  );
};

export default CreateHubPage;


